import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api/apiweb-sucursales.js';

/**
 * Sucursales del emisor para las vistas de emisión web (33, 34, 56 y 61).
 *
 * Las sucursales son de una empresa, no del usuario: si cambia el RUT emisor hay
 * que volver a pedirlas y soltar la selección anterior, porque mandar el id de una
 * sucursal de otro RUT lo rechaza el backend.
 *
 * Mientras un RUT no tenga ninguna sucursal cargada, la emisión sigue funcionando
 * como antes, con la dirección de la empresa. Apenas tiene una, elegir sucursal
 * pasa a ser obligatorio para ese RUT.
 */

// Lo habitual es emitir desde la casa matriz, que el backend marca con es_matriz
// (una sola por empresa). Con una única sucursal no hay nada que elegir. Si hay
// varias y ninguna es matriz, no se adivina: el usuario tiene que elegir, porque
// la dirección que quede acá es la que se imprime.
const sucursalPorDefecto = (sucursales) => {
  if (sucursales.length === 1) return sucursales[0];
  return sucursales.find((s) => s.es_matriz) || null;
};
const useSucursales = (rutEmisor) => {
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState('');
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    if (!rutEmisor) {
      setSucursales([]);
      setSucursalId('');
      return;
    }
    setCargando(true);
    try {
      const { data } = await api.getSucursales({ rut: rutEmisor });
      const registros = Array.isArray(data?.body) ? data.body : [];
      setSucursales(registros);
      const porDefecto = sucursalPorDefecto(registros);
      setSucursalId(porDefecto ? String(porDefecto.sucursal_id) : '');
    } catch (error) {
      // Sin lista no se puede elegir; el backend igual rechaza la emisión si el
      // emisor tiene sucursales, así que basta con dejarlo en el log.
      console.error('Error al obtener las sucursales del emisor:', error);
      setSucursales([]);
      setSucursalId('');
    } finally {
      setCargando(false);
    }
  }, [rutEmisor]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const sucursal = useMemo(
    () => sucursales.find((s) => String(s.sucursal_id) === String(sucursalId)) || null,
    [sucursales, sucursalId],
  );

  return {
    sucursales,
    sucursal,
    sucursalId,
    setSucursalId,
    cargando,
    recargar: cargar,
    // El emisor tiene sucursales cargadas pero todavía no eligió una.
    faltaSeleccion: sucursales.length > 0 && !sucursal,
  };
};

export default useSucursales;
